/**
 * Subscription management page - shows the user's Stripe subscription
 * details and links to the Stripe billing portal
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import {
  useSubscription,
  useBillingPortal,
  useBillingStatus,
} from '../services/subscription/hooks';
import { LoadingSpinner } from '../components/ui';
import { authenticatedApi } from '../lib/authenticated-api';
import { SUBSCRIPTION_LOAD_ERROR_MESSAGE } from '../lib/checkoutConflict';

import { subscriptionStatusLabel } from '../services/subscription/statusLabel';
import { heapsterLiveCheckoutState } from '../services/subscription/checkoutState';

export function SubscriptionManagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch,
  } = useSubscription();
  const billing = useBillingStatus();
  const billingStatus =
    billing.isLoading || billing.isError ? undefined : billing.data;
  // Only a successful billing read says anything about Stripe. Until then,
  // neither "no billing" claim is made and no plans are offered (#2463).
  const billingKnown = billingStatus !== undefined;
  const statusLabel = subscriptionStatusLabel(billingStatus);
  const showBillingDate =
    !!billingStatus?.has_subscription &&
    !!billingStatus.subscription_status &&
    billingStatus.subscription_status !== 'trialing';
  const { refetch: refetchBilling } = billing;
  const noBilling =
    billingStatus?.has_subscription === false &&
    heapsterLiveCheckoutState({
      signedIn: !!user,
      isLoading: subscriptionLoading,
      error: subscriptionError,
      subscription,
    }) === 'access-without-subscription';
  // An active `supapass` record backs the access, and billing confirms no
  // Stripe subscription. Redeem, manual and any other provider keep "No
  // billing to manage" (#2463).
  const managedBySupapass =
    billingStatus?.has_subscription === false &&
    Object.values(subscription?.entitlements ?? {}).some(
      e => e.isActive && e.hasSupapassSubscription
    );
  const showViewPlans =
    billingKnown &&
    !noBilling &&
    (!billingStatus.has_subscription ||
      billingStatus.subscription_status === 'canceled' ||
      billingStatus.subscription_status === 'incomplete_expired');
  const { openPortal } = useBillingPortal();
  const [isSyncing, setIsSyncing] = useState(false);

  const isLoading = subscriptionLoading;

  // Filter entitlements for Stripe-backed ones only. This is the Stripe billing
  // page (billing portal), so it must NOT show for supapass-only
  // grants — `sources` is provider-agnostic (a supapass grant also yields
  // 'subscription'), so gate on the Stripe-specific `hasStripeSubscription` flag.
  const subscriptionEntitlements = subscription?.entitlements
    ? Object.entries(subscription.entitlements)
        .filter(([, e]) => e.isActive && e.hasStripeSubscription)
        .map(([id, e]) => ({
          subscriptionId: id,
          // Stripe's own period end. `periodEndDate` is merged across every
          // source of access and is not a billing date (#2209).
          expiresAt: e.stripePeriodEndDate
            ? e.stripePeriodEndDate.toISOString()
            : null,
          willRenew: e.willRenew,
        }))
    : [];

  // The plan and its price as this user's own subscription has them
  // (`GET /v1/billing/status`), or nothing (#2463). Not the entitlement's
  // catalogue name, and not the catalogue price in the viewer's detected
  // currency, which a grandfathered subscriber or one abroad does not pay.
  const planName = billingStatus?.has_subscription
    ? billingStatus.plan_name
    : null;
  const planPrice = billingStatus?.has_subscription
    ? billingStatus.amount
    : null;

  // Helper to format expiration for display
  const getExpirationDisplay = (expiresAt: string): string =>
    new Date(expiresAt).toLocaleDateString();

  // Check if user has any active subscriptions
  const hasActiveSubscription = subscriptionEntitlements.length > 0;

  // Handle subscription sync with Stripe
  const handleSyncSubscription = async () => {
    if (!user) return;

    setIsSyncing(true);
    try {
      // Reaching here means a 2xx: `authenticatedApi` throws on every other
      // status. The old `else` branch below tested `response.data.success`,
      // which only a `200 {success:false}` could falsify — the exact shape the
      // backend no longer emits, and never should (#1731). Failures belong in
      // the catch.
      const response = await authenticatedApi.request<{
        success: boolean;
        message: string;
        hasActiveSubscription?: boolean;
      }>(`/v1/users/${user.uid}/subscription/sync`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (response.data?.hasActiveSubscription === false) {
        // No Stripe subscription found - might be a manual grant
        toast.success(
          'No Stripe subscription found - you may have a manual grant'
        );
      } else {
        toast.success('Subscription data refreshed');
      }

      // Refetch subscription data to show updated info
      await Promise.all([refetch(), refetchBilling()]);
    } catch (error) {
      console.error('Sync subscription error:', error);
      toast.error('Failed to sync subscription. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              Loading subscription details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // A failed read is not "no subscription" (#2218): this person may be
  // subscribed, so they are not told they are on the free plan or sent to the
  // plans.
  if (subscriptionError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <p className="text-red-600 dark:text-red-400">
              {SUBSCRIPTION_LOAD_ERROR_MESSAGE}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasActiveSubscription) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-700">
                <svg
                  className="h-8 w-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {!billingKnown || billingStatus.has_subscription
                ? 'Subscription'
                : managedBySupapass
                  ? 'Managed by Supapass'
                  : noBilling
                    ? 'No billing to manage'
                    : 'No Active Subscription'}
            </h1>
            {/* The subscription's plan and price come from the billing
                status, so they show with no Stripe entitlement left, as for
                a past_due subscription (#2463). */}
            {planName && <p>{planName}</p>}
            {statusLabel && <p>{statusLabel}</p>}
            {planPrice && <p>Plan price: {planPrice}</p>}
            {billingStatus?.has_subscription && (
              <button onClick={() => void openPortal()} className="btn-primary">
                Manage Billing
              </button>
            )}
            {showViewPlans && (
              <button
                onClick={() => navigate('/pricing')}
                className="btn-primary"
              >
                View Plans
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Subscription Management
              </h1>
            </div>
            <button
              onClick={() => void handleSyncSubscription()}
              disabled={isSyncing}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
              title="Refresh subscription data from Stripe"
            >
              <svg
                className={`w-5 h-5 mr-1 ${isSyncing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {isSyncing ? 'Syncing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Status Cards - iterate through subscription-sourced entitlements */}
        {subscriptionEntitlements.map(entitlement => (
          <div
            key={entitlement.subscriptionId}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Current Plan
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(planName || statusLabel) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Plan
                  </h3>
                  {planName && (
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {planName}
                    </p>
                  )}
                  {statusLabel && <p>{statusLabel}</p>}
                </div>
              )}
              {planPrice && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Plan price
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {planPrice}
                  </p>
                </div>
              )}
              {/* Only with Stripe's own date: no stand-in when it is absent
                  (#2463). */}
              {showBillingDate && entitlement.expiresAt && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {entitlement.willRenew ? 'Next Payment' : 'Expires'}
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {getExpirationDisplay(entitlement.expiresAt)}
                  </p>
                </div>
              )}
            </div>

            {/* Gated with the billing date (#2463): during a trial, and while
                billing is unknown, neither is shown. */}
            {showBillingDate && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Auto-renewal: {entitlement.willRenew ? 'On' : 'Off'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Billing portal - always show for subscription entitlements */}
        {subscriptionEntitlements.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Billing & Payments
            </h3>
            <button
              onClick={() => void openPortal()}
              className="btn-primary w-full"
            >
              Manage Billing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
